import { TableColumnsType } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { SorterResult } from 'antd/es/table/interface'
import { useState } from 'react'

import { useQueryUpdate } from './useQueryUpdate'

type Options = {
  local?: boolean
  sortFieldName?: string
}

export const useSorting = <T, R = AnyObject>(options?: Options) => {
  const [localOrder, setLocalOrder] = useState<SorterResult<T>>({})
  const { params: queryParams, updateQuery } = useQueryUpdate()
  const sortFieldKey = options?.sortFieldName ?? 'ordering'

  const triggerSort = (sorter: SorterResult<T>) => {
    const { columnKey, order } = sorter

    if (!order) {
      if (options?.local) {
        setLocalOrder({})
      } else {
        if (queryParams[sortFieldKey])
          updateQuery({ [sortFieldKey]: undefined, page: queryParams.page })
      }
      return
    }

    const ordering = order === 'ascend' ? columnKey : `-${columnKey}`

    if (options?.local) {
      setLocalOrder({
        columnKey: ordering
      })
    } else {
      updateQuery({ [sortFieldKey]: ordering, page: queryParams.page })
    }
  }

  const getSortedTableColumns = (
    columns: TableColumnsType<T>
  ): TableColumnsType<T> =>
    columns.map((column) => {
      if ('children' in column) {
        return {
          ...column,
          children: getSortedTableColumns(column.children)
        }
      }

      return {
        ...column,
        sortOrder:
          order.columnKey === column.key
            ? 'ascend'
            : order.columnKey === `-${column.key}`
            ? 'descend'
            : null
      }
    })

  const orderQueryParams = {
    ...(localOrder?.columnKey && {
      [sortFieldKey]: localOrder.columnKey as string
    }),
    ...(!options?.local && {
      [sortFieldKey]: queryParams[sortFieldKey]
    })
  } as R

  const order: SorterResult<T> = options?.local
    ? localOrder
    : { columnKey: queryParams[sortFieldKey] }

  return { order, orderQueryParams, triggerSort, getSortedTableColumns }
}
